/**
 * @file Admin.tsx
 * @description Admin panel for document management and indexation monitoring.
 * This component provides access to the admin-ui interface for users with
 * the 'can_manage_documents' permission.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, Text, Spinner, MessageBar, MessageBarType } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/authProvider";
import styles from "./Admin.module.css";

interface UserInfo {
    username: string;
    isDev: boolean;
    can_manage_documents: boolean;
    categories: string[];
}

export const Admin: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getAuthToken } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [adminUrl, setAdminUrl] = useState<string>("");

    useEffect(() => {
        checkPermissions();
    }, []);

    const checkPermissions = async () => {
        try {
            const token = await getAuthToken();
            const headers: HeadersInit = {
                "Content-Type": "application/json"
            };

            if (token) {
                headers["Authorization"] = `Bearer ${token}`;
            }

            // Check if user has permission to manage documents
            const response = await fetch("/user_categories_postgresql", {
                method: "GET",
                headers: headers
            });

            if (!response.ok) {
                throw new Error("Failed to fetch user information");
            }

            const data = await response.json();

            // Check if user has document management permission
            if (!data.can_manage_documents) {
                setError(t("admin.noPermission") || "You don't have permission to access this page");
                setLoading(false);
                return;
            }

            setUserInfo(data);

            // Get the admin-ui URL from environment or use default
            const backendUrl = import.meta.env.VITE_BACKEND_URL || "";
            const adminBackendUrl = import.meta.env.VITE_ADMIN_FRONTEND_URL || "http://localhost:3000";

            // Build admin URL with token
            const url = `${adminBackendUrl}?token=${encodeURIComponent(token || "")}`;
            setAdminUrl(url);
            setLoading(false);
        } catch (err) {
            console.error("Error checking permissions:", err);
            setError(t("admin.errorLoading") || "Error loading admin panel");
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Stack className={styles.container} verticalAlign="center" horizontalAlign="center">
                <Spinner label={t("admin.loading") || "Loading admin panel..."} />
            </Stack>
        );
    }

    if (error || !userInfo?.can_manage_documents) {
        return (
            <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
                <MessageBar messageBarType={MessageBarType.error}>{error || t("admin.accessDenied")}</MessageBar>
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <button onClick={() => navigate("/chat")} className={styles.backButton}>
                        {t("admin.backToChat") || "Back to Chat"}
                    </button>
                </Stack>
            </Stack>
        );
    }

    return (
        <Stack className={styles.adminContainer}>
            {/* <Stack className={styles.header} horizontal horizontalAlign="space-between" verticalAlign="center">
                <Stack horizontal tokens={{ childrenGap: 15 }} verticalAlign="center">
                    <Text variant="xLarge" className={styles.title}>
                        {"Document Administration"}
                    </Text>
                    <Text variant="medium" className={styles.username}>
                        {userInfo.username}
                    </Text>
                </Stack>
            </Stack> */}

            <div className={styles.iframeContainer}>
                {adminUrl && (
                    <iframe src={adminUrl} className={styles.iframe} title="Admin Panel" sandbox="allow-same-origin allow-scripts allow-forms allow-popups" />
                )}
            </div>

            <MessageBar messageBarType={MessageBarType.info} className={styles.infoBar}>
                {t("admin.infoMessage") || "This interface allows you to manage document indexation, monitor jobs, and browse Azure Storage."}
            </MessageBar>
        </Stack>
    );
};

export default Admin;
